import { useMemo, useCallback } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { useUserStateContext } from '@/shared/components/UserStateProvider';

interface VenueAwareTimeFormatterOptions {
  /**
   * Format type for the date
   * - 'short': Just the date (e.g., "Jan 15")
   * - 'long': Full date with time (default)
   * - 'time-only': Just the time (e.g., "3:30 PM")
   */
  formatType?: 'short' | 'long' | 'time-only';

  /**
   * Whether to show relative dates like "Today", "Tomorrow", "Yesterday"
   * @default true
   */
  showRelative?: boolean;

  /**
   * Whether to show timezone abbreviation (e.g., "EST")
   * Only shown when the timezone differs from browser timezone
   * @default true
   */
  showTimezone?: boolean;

  /**
   * Optional venue timezone to use if useVenueTimezone is enabled
   * If not provided, falls back to user timezone
   */
  venueTimezone?: string | null;
}

const TIME_FORMATS = {
  SHORT_DATE: 'MMM dd',
  FULL_DATE_TIME: 'MMM dd, yyyy • h:mmaaa',
  FULL_DATE_TIME_TZ: 'MMM dd, yyyy • h:mmaaa zzz',
  TIME_ONLY: 'h:mm a zzz',
} as const;

/**
 * Custom hook that provides time formatting functions with venue timezone awareness
 *
 * This hook can be used both inside and outside of CurrentGameProvider.
 * When used outside (e.g., in game lists), you can pass the venue timezone as an option.
 *
 * This hook respects the user's `useVenueTimezone` preference:
 * - If true and venueTimezone is provided: formats using the venue's timezone
 * - If false or no venueTimezone: formats using the user's timezone
 *
 * @example
 * ```tsx
 * // Inside game page (with CurrentGameProvider)
 * const { formatTime } = useVenueAwareTimeFormatter();
 * const formatted = formatTime(date);
 *
 * // Outside game page (e.g., GameCard in list)
 * const { formatTime } = useVenueAwareTimeFormatter();
 * const formatted = formatTime(date, { venueTimezone: game.venue.timezone });
 * ```
 */
export const useVenueAwareTimeFormatter = () => {
  const { userProfile } = useUserStateContext();

  const browserTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );

  /**
   * Checks if a date is valid
   */
  const isValidDate = (date: any): date is Date => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  /**
   * Gets relative day format (Today, Tomorrow, Yesterday)
   * This is timezone-aware - it compares dates in the target timezone, not browser timezone
   */
  const getRelativeDayFormat = (targetDate: Date, tz: string, includeTime: boolean) => {
    try {
      // Get current date in the TARGET timezone
      const nowInTz = formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
      const targetInTz = formatInTimeZone(targetDate, tz, 'yyyy-MM-dd');

      // Calculate tomorrow and yesterday in the target timezone
      const tomorrowDate = new Date();
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);
      const tomorrowInTz = formatInTimeZone(tomorrowDate, tz, 'yyyy-MM-dd');

      const yesterdayDate = new Date();
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      const yesterdayInTz = formatInTimeZone(yesterdayDate, tz, 'yyyy-MM-dd');

      if (targetInTz === nowInTz) {
        return includeTime
          ? `Today • ${formatInTimeZone(targetDate, tz, TIME_FORMATS.TIME_ONLY)}`
          : 'Today';
      }
      if (targetInTz === tomorrowInTz) {
        return includeTime
          ? `Tomorrow • ${formatInTimeZone(targetDate, tz, TIME_FORMATS.TIME_ONLY)}`
          : 'Tomorrow';
      }
      if (targetInTz === yesterdayInTz) {
        return includeTime
          ? `Yesterday • ${formatInTimeZone(targetDate, tz, TIME_FORMATS.TIME_ONLY)}`
          : 'Yesterday';
      }
      return null;
    } catch (error) {
      console.error('Error in getRelativeDayFormat:', error);
      return null;
    }
  };

  /**
   * Determines which timezone to use based on user preference and options
   */
  const getActiveTimezone = useCallback(
    (venueTimezone?: string | null): string => {
 
      // If useVenueTimezone is enabled and venue timezone is provided, use it
      if (userProfile?.useVenueTimezone && venueTimezone) {
        return venueTimezone;
      }

      // Otherwise use the user's timezone, fallback to browser timezone
      // Handle 'UNSET' as a special case meaning no timezone is set
      const userTz = userProfile?.tz;
      if (!userTz || userTz === 'UNSET') {
        return browserTimezone;
      }

      return userTz;
    },
    [userProfile?.useVenueTimezone, userProfile?.tz, browserTimezone]
  );

  /**
   * Formats a date/time with venue timezone awareness
   *
   * @param date - The date to format
   * @param options - Formatting options including optional venueTimezone
   * @returns Formatted date string
   */
  const formatTime = useCallback(
    (
      date: Date | string | null | undefined,
      options: VenueAwareTimeFormatterOptions = {}
    ): string => {
      const {
        formatType = 'long',
        showRelative = true,
        showTimezone = true,
        venueTimezone,
      } = options;

      // Handle null/undefined
      if (!date) return '';

      // Convert string to Date if needed
      const targetDate = typeof date === 'string' ? new Date(date) : date;

      // Validate date
      if (!isValidDate(targetDate)) {
        console.error('Invalid date provided to formatTime:', date);
        return '';
      }

      try {
        const tz = getActiveTimezone(venueTimezone);

        // Show relative dates if enabled and not time-only format
        if (showRelative && formatType !== 'time-only') {
          const relativeFormat = getRelativeDayFormat(
            targetDate,
            tz,
            formatType !== 'short'
          );
          if (relativeFormat) return relativeFormat;
        }

        // Determine format string based on options
        let formatString: string;

        if (formatType === 'short') {
          formatString = TIME_FORMATS.SHORT_DATE;
        } else if (formatType === 'time-only') {
          formatString = TIME_FORMATS.TIME_ONLY;
        } else {
          // Show timezone abbreviation only if timezone differs from browser and showTimezone is true
          const shouldShowTz = showTimezone && browserTimezone !== tz;
          formatString = shouldShowTz
            ? TIME_FORMATS.FULL_DATE_TIME_TZ
            : TIME_FORMATS.FULL_DATE_TIME;
        }

        return formatInTimeZone(targetDate, tz, formatString);
      } catch (error) {
        console.error('Error in formatTime:', error);
        return '';
      }
    },
    [getActiveTimezone, browserTimezone]
  );

  return {
    formatTime,
    getActiveTimezone,
    browserTimezone,
  };
};
